import AssignmentIcon from "@mui/icons-material/Assignment";
import CheckIcon from "@mui/icons-material/Check";
import ClearIcon from "@mui/icons-material/Clear";
import RefreshIcon from "@mui/icons-material/Refresh";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import {
  Box,
  ButtonGroup,
  Dialog,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { Button, DialogTitle } from "common";
import I18n from "i18n";
import moment from "moment";
import { useState } from "react";
import { BookingItem } from "./bookingApi";
import useBookingReadyActions from "./useBookingReadyActions";

// Custom split circle icon component
const SplitCircleIcon = ({
  leftReady,
  rightReady,
}: {
  leftReady: boolean;
  rightReady: boolean;
}) => {
  const theme = useTheme();
  const leftColor = leftReady
    ? theme.palette.success.dark
    : theme.palette.action.disabled;
  const rightColor = rightReady
    ? theme.palette.success.light
    : theme.palette.action.disabled;

  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      style={{ display: "block" }}
    >
      {/* Left half - Booking Ready */}
      <path
        d="M12 2 A10 10 0 0 0 12 22 L12 12 Z"
        fill={leftColor}
        stroke="#fff"
        strokeWidth="1"
      />
      {/* Right half - Food Ready */}
      <path
        d="M12 2 A10 10 0 0 1 12 22 L12 12 Z"
        fill={rightColor}
        stroke="#fff"
        strokeWidth="1"
      />
      {/* Center divider */}
      <line x1="12" y1="2" x2="12" y2="22" stroke="#fff" strokeWidth="2" />
    </svg>
  );
};

// Button groups component
const ReadyButtonGroups = ({
  booking,
  bookingReady,
  foodReady,
  readyActionLoading,
  setBookingReadyStatus,
  setFoodReadyStatus,
  size,
}: {
  booking: BookingItem;
  bookingReady: boolean;
  foodReady: boolean;
  readyActionLoading: string | null;
  setBookingReadyStatus: (value: boolean) => void;
  setFoodReadyStatus: (value: boolean) => void;
  size: "small" | "medium";
}) => {
  const lastBookingEntry = booking.booking_ready_history?.length
    ? booking.booking_ready_history[booking.booking_ready_history.length - 1]
    : undefined;
  const lastFoodEntry = booking.food_ready_history?.length
    ? booking.food_ready_history[booking.food_ready_history.length - 1]
    : undefined;
  const [historyOpen, setHistoryOpen] = useState<"booking" | "food" | null>(
    null,
  );

  return (
    <>
      <div style={{ display: "flex", gap: "12px", alignItems: "center" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            alignSelf: "stretch",
          }}
        >
          <ButtonGroup size="small" variant="outlined" color="success">
            <Button
              size={size}
              variant={bookingReady ? "contained" : "outlined"}
              color="success"
              sx={{ minWidth: "40px" }}
            >
              <AssignmentIcon fontSize="small" />
              {bookingReady && (
                <CheckIcon
                  sx={{
                    position: "absolute",
                    fontSize: "13px",
                    top: 0,
                    right: 0,
                    color: "inherit",
                  }}
                />
              )}
              {!bookingReady && lastBookingEntry && (
                <ClearIcon
                  sx={{
                    position: "absolute",
                    fontSize: "13px",
                    top: 0,
                    right: 0,
                    color: "error.main",
                  }}
                />
              )}
            </Button>
            {!bookingReady && (
              <Button
                size={size}
                title={I18n.t("Booking.bookingReadyButton")}
                disabled={readyActionLoading !== null}
                onClick={() => setBookingReadyStatus(true)}
                loading={readyActionLoading === "booking-set"}
                variant="outlined"
                color="success"
                startIcon={<CheckIcon />}
              />
            )}
            {bookingReady && (
              <Button
                size={size}
                title={I18n.t("Booking.bookingReadyRecheck")}
                disabled={readyActionLoading !== null}
                onClick={() => setBookingReadyStatus(true)}
                loading={readyActionLoading === "booking-set"}
                variant="outlined"
                color="success"
                startIcon={<RefreshIcon />}
              />
            )}
            {bookingReady && (
              <Button
                size={size}
                title={I18n.t("Booking.bookingReadyUnset")}
                disabled={readyActionLoading !== null}
                onClick={() => setBookingReadyStatus(false)}
                loading={readyActionLoading === "booking-unset"}
                variant="outlined"
                color="success"
                sx={{ minWidth: "40px", px: 1 }}
              >
                <ClearIcon fontSize="small" />
              </Button>
            )}
          </ButtonGroup>
          {(lastBookingEntry || !!booking.booking_ready_at) && (
            <Typography
              variant="caption"
              color="textSecondary"
              onClick={() => setHistoryOpen("booking")}
              sx={{
                cursor: "pointer",
                textDecoration: "underline",
                textDecorationStyle: "dotted",
              }}
            >
              {moment(lastBookingEntry?.at ?? booking.booking_ready_at).format(
                "L LT",
              )}
              {lastBookingEntry?.by && ` – ${lastBookingEntry.by}`}
            </Typography>
          )}
        </div>
        <Divider orientation="vertical" flexItem sx={{ height: "32px" }} />

        {/* Food Ready Group */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            alignSelf: "stretch",
          }}
        >
          <ButtonGroup
            size="small"
            variant="outlined"
            color="success"
            sx={{
              "& .MuiButton-root": {
                borderColor: "success.light",
              },
              "& .MuiButton-contained": { bgcolor: "success.light" },
            }}
          >
            <Button
              variant={foodReady ? "contained" : "outlined"}
              color={foodReady ? "success" : "success"}
              size={size}
              sx={{
                minWidth: "40px",
                borderColor: "success.light",
              }}
            >
              <RestaurantIcon fontSize="small" />
              {foodReady && (
                <CheckIcon
                  sx={{
                    position: "absolute",
                    fontSize: "13px",
                    top: 0,
                    right: 0,
                    color: "inherit",
                  }}
                />
              )}
              {!foodReady && lastFoodEntry && (
                <ClearIcon
                  sx={{
                    position: "absolute",
                    fontSize: "13px",
                    top: 0,
                    right: 0,
                    color: "error.main",
                  }}
                />
              )}
            </Button>
            {!foodReady && (
              <Button
                size={size}
                title={I18n.t("Booking.foodReadyButton")}
                disabled={readyActionLoading !== null}
                onClick={() => setFoodReadyStatus(true)}
                loading={readyActionLoading === "food-set"}
                variant="outlined"
                startIcon={<CheckIcon />}
                color="success"
                sx={{ color: "success.light", borderColor: "success.light" }}
              />
            )}
            {foodReady && (
              <Button
                size={size}
                title={I18n.t("Booking.foodReadyRecheck")}
                disabled={readyActionLoading !== null}
                onClick={() => setFoodReadyStatus(true)}
                loading={readyActionLoading === "food-set"}
                variant="outlined"
                startIcon={<RefreshIcon />}
                color="success"
                sx={{ color: "success.light", borderColor: "success.light" }}
              />
            )}
            {foodReady && (
              <Button
                size={size}
                title={I18n.t("Booking.foodReadyUnset")}
                disabled={readyActionLoading !== null}
                onClick={() => setFoodReadyStatus(false)}
                loading={readyActionLoading === "food-unset"}
                variant="outlined"
                sx={{
                  minWidth: "40px",
                  px: 1,
                  color: "success.light",
                  borderColor: "success.light",
                }}
                color="success"
              >
                <ClearIcon fontSize="small" />
              </Button>
            )}
          </ButtonGroup>
          {(lastFoodEntry || !!booking.food_ready_at) && (
            <Typography
              variant="caption"
              color="textSecondary"
              onClick={() => setHistoryOpen("food")}
              sx={{
                cursor: "pointer",
                textDecoration: "underline",
                textDecorationStyle: "dotted",
              }}
            >
              {moment(lastFoodEntry?.at ?? booking.food_ready_at).format(
                "L LT",
              )}
              {lastFoodEntry?.by && ` – ${lastFoodEntry.by}`}
            </Typography>
          )}
        </div>
      </div>

      <Dialog open={historyOpen !== null} onClose={() => setHistoryOpen(null)}>
        <DialogTitle onClose={() => setHistoryOpen(null)}>
          {historyOpen === "booking"
            ? "Foglalás előzmények"
            : "Étel előzmények"}
        </DialogTitle>
        <DialogContent sx={{ p: 3, minWidth: 280 }}>
          {historyOpen === "booking" &&
            (booking.booking_ready_history ?? []).map((h, i) => (
              <Box
                key={i}
                sx={{
                  display: "flex",
                  gap: 0.5,
                  alignItems: "center",
                  mb: 0.5,
                }}
              >
                <Typography
                  variant="caption"
                  color={h.ready ? "success.dark" : "error.main"}
                  sx={{ fontWeight: 700, minWidth: 12 }}
                >
                  {h.ready ? "✓" : "✗"}
                </Typography>
                <Typography variant="caption">
                  {moment(h.at).format("L LT")} – {h.by}
                </Typography>
              </Box>
            ))}
          {historyOpen === "food" &&
            (booking.food_ready_history ?? []).map((h, i) => (
              <Box
                key={i}
                sx={{
                  display: "flex",
                  gap: 0.5,
                  alignItems: "center",
                  mb: 0.5,
                }}
              >
                <Typography
                  variant="caption"
                  color={h.ready ? "success.light" : "error.main"}
                  sx={{ fontWeight: 700, minWidth: 12 }}
                >
                  {h.ready ? "✓" : "✗"}
                </Typography>
                <Typography variant="caption">
                  {moment(h.at).format("L LT")} – {h.by}
                </Typography>
              </Box>
            ))}
        </DialogContent>
        <DialogActions>
          <Button
            title={I18n.t("App.close")}
            onClick={() => setHistoryOpen(null)}
          />
        </DialogActions>
      </Dialog>
    </>
  );
};

export interface BookingReadyActionsProps {
  booking: BookingItem;
  onSuccess?: (updatedBooking: Partial<BookingItem>) => void;
  variant?: "icon" | "buttons";
  size?: "small" | "medium";
}

export default function BookingReadyActions({
  booking,
  onSuccess,
  variant = "buttons",
  size = "small",
}: BookingReadyActionsProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const { readyActionLoading, setBookingReadyStatus, setFoodReadyStatus } =
    useBookingReadyActions({
      bookingId: booking.id,
      onSuccess: updatedData => {
        onSuccess && onSuccess(updatedData);
        if (variant === "icon") {
          //setDialogOpen(false);
        }
      },
    });

  // Status calculation for icon variant
  const bookingReady = booking.booking_ready_history?.length
    ? booking.booking_ready_history[booking.booking_ready_history.length - 1]
        .ready
    : !!booking.booking_ready_at;
  const foodReady = booking.food_ready_history?.length
    ? booking.food_ready_history[booking.food_ready_history.length - 1].ready
    : !!booking.food_ready_at;

  if (variant === "icon") {
    const tooltipText = `Foglalás: ${
      bookingReady ? "Kész" : "Nincs kész"
    } | Étel: ${foodReady ? "Kész" : "Nincs kész"}`;

    return (
      <>
        <IconButton
          title={tooltipText}
          size="small"
          onClick={() => setDialogOpen(true)}
        >
          <SplitCircleIcon leftReady={bookingReady} rightReady={foodReady} />
        </IconButton>

        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)}>
          <DialogTitle onClose={() => setDialogOpen(false)}>
            Fogalalás ellenőrzése
            <Typography variant="body2" color="textSecondary">
              {booking.name}
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ p: 3 }}>
            <ReadyButtonGroups
              booking={booking}
              bookingReady={bookingReady}
              foodReady={foodReady}
              readyActionLoading={readyActionLoading}
              setBookingReadyStatus={setBookingReadyStatus}
              setFoodReadyStatus={setFoodReadyStatus}
              size={size}
            />
            {((booking.booking_ready_history?.length ?? 0) > 0 ||
              (booking.food_ready_history?.length ?? 0) > 0) && (
              <Box sx={{ mt: 2, display: "flex", gap: 3 }}>
                {(booking.booking_ready_history?.length ?? 0) > 0 && (
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      Foglalás előzmények
                    </Typography>
                    {booking.booking_ready_history!.map((h, i) => (
                      <Box
                        key={i}
                        sx={{ display: "flex", gap: 0.5, alignItems: "center" }}
                      >
                        <Typography
                          variant="caption"
                          color={h.ready ? "success.dark" : "error.main"}
                          sx={{ fontWeight: 700, minWidth: 12 }}
                        >
                          {h.ready ? "✓" : "✗"}
                        </Typography>
                        <Typography variant="caption">
                          {moment(h.at).format("L LT")} – {h.by}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
                {(booking.food_ready_history?.length ?? 0) > 0 && (
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      Étel előzmények
                    </Typography>
                    {booking.food_ready_history!.map((h, i) => (
                      <Box
                        key={i}
                        sx={{ display: "flex", gap: 0.5, alignItems: "center" }}
                      >
                        <Typography
                          variant="caption"
                          color={h.ready ? "success.light" : "error.main"}
                          sx={{ fontWeight: 700, minWidth: 12 }}
                        >
                          {h.ready ? "✓" : "✗"}
                        </Typography>
                        <Typography variant="caption">
                          {moment(h.at).format("L LT")} – {h.by}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button
              title={I18n.t("App.close")}
              onClick={() => setDialogOpen(false)}
            />
          </DialogActions>
        </Dialog>
      </>
    );
  }

  // Buttons variant (for form)
  return (
    <ReadyButtonGroups
      booking={booking}
      bookingReady={bookingReady}
      foodReady={foodReady}
      readyActionLoading={readyActionLoading}
      setBookingReadyStatus={setBookingReadyStatus}
      setFoodReadyStatus={setFoodReadyStatus}
      size={size}
    />
  );
}
